









import React, { useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { FiTruck, FiArrowLeft, FiRotateCcw, FiAlertCircle, FiXCircle } from "react-icons/fi";
import { toast } from "react-toastify";
import { apiDELETE, apiPOST } from "../utilities/apiHelpers";

const OrderTrackingpage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showReverseModal, setShowReverseModal] = useState(false);
  const [loading, setLoading] = useState(false);


  // Data passed from the Orders Table state
  const shipmentData = location.state?.trackingData;
  const orderIdFromTable = location.state?.orderId;

  if (!shipmentData) {
    return (
      <div className="p-10 text-center">
        <p className="text-gray-500 mb-4">Tracking data not found. Please try again from the Orders list.</p>
        <button onClick={() => navigate(-1)} className="text-[#14967F] font-bold underline">
          Back to Orders
        </button>
      </div>
    );
  }

  const { order_number, wbn, track } = shipmentData;

  const journeyScans = track?.details || [];
  const deliveredIndex = journeyScans.findIndex(e => e.status.toLowerCase() === "delivered");
  const timelineEvents = deliveredIndex >= 0 ? journeyScans.slice(0, deliveredIndex + 1) : journeyScans;

  const statusLower = track?.status?.toLowerCase() || "";
  const isDelivered = statusLower === "delivered";

  // ✅ The condition you requested: Only show Cancel for these two states
  const canCancel = statusLower === "order placed" || statusLower === "ready for pickup";

  // --- REVERSE SHIPMENT HANDLER ---
  const handleRaiseReverseRequest = async () => {
    const cleanWaybill = wbn?.trim() || id?.trim();
    if (!cleanWaybill) return toast.error("Waybill number is missing");

    setLoading(true);
    try {
      const payload = {
        waybill: cleanWaybill,
        order_number: orderIdFromTable || order_number
      };
      const response = await apiPOST(`/v1/shipment/reverse/${cleanWaybill}`, payload);

      if (response?.data?.success || response?.status === 200) {
        toast.success("Reverse Request Raised Successfully!");
        setShowReverseModal(false);
        navigate(-1);
      } else {
        toast.error(response?.data?.message || "Failed to raise request.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Internal Server Error");
    } finally {
      setLoading(false);
    }
  };

  // --- CANCEL SHIPMENT HANDLER ---
  const handleCancelShipment = async () => {
    setLoading(true);
    try {
      await apiDELETE(`/v1/shipment/cancel/${wbn}`, {
        data: { orderCancelByUser: true }
      });
      toast.success("Shipment cancelled successfully.");
      setShowCancelModal(false);
      navigate(-1)
      setTimeout(() => {
        window.location.reload();
      }, 100);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Cancel failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-4 min-h-screen">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 mb-6 text-white bg-[#14967F] hover:bg-[#0d6e5d] px-4 py-2 rounded shadow transition-all"
      >
        <FiArrowLeft /> Back to Orders
      </button>

      {/* Info Card */}
      <div className="bg-white shadow-lg rounded-xl p-6 mb-8 border-l-4 border-[#14967F] flex flex-col md:flex-row justify-between items-center">
        <div className="space-y-1 w-full">
          <p><span className="font-bold text-[#14967F]">Customer/Order:</span> {order_number}</p>
          <p><span className="font-bold text-[#14967F]">Waybill:</span> {wbn}</p>
          <p><span className="font-bold text-[#14967F]">Status:</span> <span className="uppercase font-semibold">{track?.status}</span></p>
        </div>

        <div className="flex flex-col items-center md:items-end gap-3 mt-4 md:mt-0 w-full">
          <div className="flex items-center">
            <FiTruck size={28} className="text-[#14967F] mr-2" />
            <span className="text-[#14967F] font-black text-xl uppercase">
              {track?.status?.toLowerCase() === 'seller cancelled' ||
                track?.status?.toLowerCase() === 'seller canceled'
                ? 'Cancelled'
                : track?.status}
            </span>
          </div>

          <div className="flex gap-3">
            {/* Show Reverse button if Delivered */}
            {isDelivered && (
              <button
                onClick={() => setShowReverseModal(true)}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2 rounded-lg font-bold shadow-md transition-all uppercase text-sm bg-orange-500 hover:bg-orange-600 text-white"
              >
                <FiRotateCcw className={loading ? "animate-spin" : ""} />
                {loading ? "Please wait..." : "Reverse Order"}
              </button>
            )}

            {/* Show Cancel button if in Place/Ready states */}
            {canCancel && (
              <button
                onClick={() => setShowCancelModal(true)}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2 rounded-lg font-bold shadow-md transition-all uppercase text-sm bg-red-600 hover:bg-red-700 text-white"
              >
                <FiXCircle /> {loading ? "..." : "Cancel Order"}
              </button>
            )}
          </div>

          {/* Fallback text if neither action is available */}
          {!isDelivered && !canCancel && (
            <p className="text-xs text-gray-400 italic font-medium">Order is cancelled.</p>
          )}
        </div>
      </div>

      {/* --- REVERSE MODAL --- */}
      {showReverseModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm text-center shadow-xl">
            <FiAlertCircle size={50} className="text-orange-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Initiate Return</h2>
            <p className="text-gray-600 mb-6 font-medium text-sm">Create a return pickup for <br /> <b>{wbn}</b>?</p>
            <div className="flex gap-3">
              <button onClick={() => setShowReverseModal(false)} className="flex-1 px-4 py-2 bg-gray-100 rounded font-bold text-gray-600">Close</button>
              <button onClick={handleRaiseReverseRequest} className="flex-1 px-4 py-2 bg-orange-500 text-white rounded font-bold hover:bg-orange-600">Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* --- CANCEL MODAL --- */}
      {showCancelModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm text-center shadow-xl">
            <FiAlertCircle size={50} className="text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Cancel Shipment?</h2>
            <p className="text-gray-600 mb-6 font-medium text-sm">Are you sure you want to cancel <br /> <b>{wbn}</b>?</p>
            <div className="flex gap-3">
              <button onClick={() => setShowCancelModal(false)} className="flex-1 px-4 py-2 bg-gray-100 rounded font-bold text-gray-600">No, Back</button>
              <button onClick={handleCancelShipment} className="flex-1 px-4 py-2 bg-red-600 text-white rounded font-bold hover:bg-red-700">Yes, Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="bg-white shadow-lg rounded-xl p-6 border-l-4 border-[#14967F]">
        <h3 className="text-lg font-bold mb-6 text-[#14967F] uppercase">Shipment Timeline</h3>
        <div className="relative ml-4">
          <div className="absolute top-0 left-2 w-0.5 mt-2 bg-gray-200 h-full"></div>
          {timelineEvents.map((event, index) => (
            <div key={index} className="mb-8 relative pl-10">
              <div className={`absolute left-0 top-1 w-5 h-5 rounded-full border-2 border-white shadow-sm ${event.status.toLowerCase() === "delivered" ? "bg-black" : "bg-[#14967F]"
                }`}></div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                <p className="font-bold text-[#14967F] uppercase text-sm">{event.status}</p>
                <p className="text-sm text-gray-600">📍 {event.location}</p>
                <p className="text-[10px] text-gray-400 mt-1">{moment(event.ctime).format("DD MMM YYYY, hh:mm A")}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OrderTrackingpage;